import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Download, TrendingUp, TrendingDown, Users, Store, Building2,
  Euro, Target, BarChart3, PieChart as PieChartIcon, Calendar, Shield,
  Rocket, AlertTriangle, CheckCircle2, FileText
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';

const COLORS = ['#0F172A', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6'];

const BusinessPlanPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [businessPlan, setBusinessPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchBusinessPlan();
  }, []);

  const fetchBusinessPlan = async () => {
    try {
      const response = await api.get('/admin/business-plan');
      setBusinessPlan(response.data);
    } catch (error) {
      console.error('Error fetching business plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!businessPlan) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Errore nel caricamento del Business Plan</p>
      </div>
    );
  }

  // Prepare chart data
  const yearlyChartData = businessPlan.yearly_projections.map(y => ({
    year: `Anno ${y.year}`,
    utenti: y.metrics.active_users,
    aziende: y.metrics.total_companies,
    revenue: y.revenue.total_revenue,
    costi: y.costs.total_costs,
    margine: y.profitability.gross_margin
  }));

  const categoryData = businessPlan.yearly_projections[2]?.categories.map(c => ({
    name: c.name,
    value: c.monthly_commission * 12,
    companies: c.companies
  })) || [];

  const penetrationData = businessPlan.yearly_projections.map(y => ({
    year: `Anno ${y.year}`,
    condomini: y.metrics.condo_penetration_pct,
    utenti: y.metrics.user_penetration_pct
  }));

  return (
    <div className="min-h-screen bg-white print:bg-white" ref={printRef}>
      {/* Header - Hidden in print */}
      <header className="bg-slate-900 text-white sticky top-0 z-40 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/admin')} className="text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
            <div className="flex items-center gap-4">
              {/* PDF button removed */}
            </div>
          </div>
        </div>
      </header>

      {/* Print Header */}
      <div className="hidden print:block p-8 border-b-4 border-amber-500">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 font-serif">{businessPlan.metadata.title}</h1>
            <p className="text-xl text-slate-600 mt-2">{businessPlan.metadata.subtitle}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Versione {businessPlan.metadata.version}</p>
            <p className="text-sm text-slate-500">{businessPlan.metadata.date}</p>
            <Badge className="mt-2 bg-red-100 text-red-700">CONFIDENZIALE</Badge>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:px-0">
        {/* Cover Section */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-12 text-white print:rounded-none">
            <div className="max-w-3xl">
              <Badge className="bg-amber-500 text-white mb-6">BUSINESS PLAN 2024-2029</Badge>
              <h1 className="text-5xl font-bold font-serif mb-6 print:text-4xl">{businessPlan.metadata.title}</h1>
              <p className="text-xl text-slate-300 mb-8">{businessPlan.metadata.subtitle}</p>
              <p className="text-lg text-slate-400 leading-relaxed">
                {businessPlan.executive_summary.mission}
              </p>
            </div>
          </div>

          {/* Key Numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Mercato Target</p>
                <p className="text-3xl font-bold text-slate-900">{formatNumber(businessPlan.base_parameters.total_housing_units)}</p>
                <p className="text-sm text-slate-500">unità abitative</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Revenue Anno 5</p>
                <p className="text-3xl font-bold text-emerald-600">{formatCurrency(businessPlan.yearly_projections[4]?.revenue.total_revenue || 0)}</p>
                <p className="text-sm text-slate-500">proiezione</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Utenti Anno 5</p>
                <p className="text-3xl font-bold text-blue-600">{formatNumber(businessPlan.yearly_projections[4]?.metrics.active_users || 0)}</p>
                <p className="text-sm text-slate-500">attivi</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <p className="text-sm text-slate-500">Break-Even</p>
                <p className="text-3xl font-bold text-purple-600">Anno 1</p>
                <p className="text-sm text-slate-500">Q4</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Executive Summary */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <FileText className="w-8 h-8 text-amber-500" />
            Executive Summary
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-500" />
                  Opportunità
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed">
                  {businessPlan.executive_summary.opportunity}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-emerald-500" />
                  Modello di Business
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed">
                  {businessPlan.executive_summary.model}
                </p>
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700">Commissione per transazione:</p>
                  <p className="text-2xl font-bold text-slate-900">€{businessPlan.base_parameters.commission_fixed} + {businessPlan.base_parameters.commission_percentage}%</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-blue-500" />
                  Richiesta Investimento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {businessPlan.executive_summary.ask}
                </p>
                <div className="space-y-2">
                  {Object.entries(businessPlan.investment).map(([key, inv]) => (
                    <div key={key} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span className="text-sm font-medium">{key.replace('_', ' ').toUpperCase()}</span>
                      <span className="font-bold text-emerald-600">{formatCurrency(inv.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-500" />
                  Vantaggi Competitivi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {businessPlan.market_analysis.unique_value_proposition.map((uvp, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-slate-600">{uvp}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Commission Model - DETAILED SECTION */}
        {businessPlan.commission_model && (
          <section className="mb-12 print:mb-8 print:break-after-page">
            <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
              <Euro className="w-8 h-8 text-amber-500" />
              Modello di Commissioni
            </h2>

            {/* Overview */}
            <Card className="mb-8 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">{businessPlan.commission_model.title}</h3>
                <p className="text-lg text-slate-300 leading-relaxed">
                  {businessPlan.commission_model.overview}
                </p>
                <div className="mt-6 p-6 bg-white/10 rounded-xl">
                  <p className="text-sm text-slate-400 uppercase tracking-wider mb-2">Formula Commissione</p>
                  <p className="text-3xl font-bold text-amber-400">{businessPlan.commission_model.commission_structure.formula}</p>
                </div>
              </CardContent>
            </Card>

            {/* Who Pays What */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="border-emerald-200">
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    <Store className="w-5 h-5" />
                    Costi per l'Azienda
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-slate-600 mb-4">{businessPlan.commission_model.who_pays.azienda.description}</p>
                  <div className="space-y-3">
                    {businessPlan.commission_model.who_pays.azienda.costs.map((cost, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{cost.item}</p>
                          <p className="text-xs text-slate-500">{cost.note}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{typeof cost.amount === 'number' ? `€${cost.amount}` : cost.amount}</p>
                          <p className="text-xs text-slate-500">{cost.frequency}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                    <p className="text-sm text-emerald-800">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      <strong>Nessuna commissione sulle transazioni!</strong> {businessPlan.commission_model.who_pays.azienda.note}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-200">
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Users className="w-5 h-5" />
                    Costi per il Condomino
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-slate-600 mb-4">{businessPlan.commission_model.who_pays.condomino.description}</p>
                  <div className="p-6 bg-blue-50 rounded-xl text-center">
                    <p className="text-5xl font-bold text-blue-600 mb-2">€0</p>
                    <p className="text-slate-600">Nessun costo di iscrizione o utilizzo</p>
                  </div>
                  <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {businessPlan.commission_model.who_pays.condomino.note}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Commission Structure */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Struttura della Commissione</CardTitle>
                <p className="text-slate-500">{businessPlan.commission_model.commission_structure.description}</p>
              </CardHeader>
              <CardContent>
                {/* Main Formula */}
                <div className="p-6 bg-slate-100 rounded-xl mb-6">
                  <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Formula Standard</p>
                  <p className="text-2xl font-bold text-slate-900">{businessPlan.commission_model.commission_structure.formula}</p>
                </div>
                
                {/* Small Transaction Protection */}
                {businessPlan.commission_model.commission_structure.formula_small && (
                  <div className="p-6 bg-emerald-100 rounded-xl mb-6 border-2 border-emerald-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      <p className="text-sm text-emerald-700 uppercase tracking-wider font-semibold">Protezione Piccole Spese</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-800">{businessPlan.commission_model.commission_structure.formula_small}</p>
                    <p className="text-sm text-emerald-700 mt-2">
                      Per transazioni tra €{businessPlan.commission_model.commission_structure.thresholds?.small_transaction_min} e €{businessPlan.commission_model.commission_structure.thresholds?.small_transaction_max}, 
                      l'utente riceve <strong>SEMPRE almeno il {businessPlan.commission_model.commission_structure.thresholds?.min_user_percentage}%</strong> del cashback.
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-3 gap-6">
                  {businessPlan.commission_model.commission_structure.components.map((comp, i) => (
                    <div key={i} className={`p-6 border-2 rounded-xl ${
                      comp.name.includes('Protezione') ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-lg">{comp.name}</h4>
                        <span className={`text-3xl font-bold ${comp.name.includes('Protezione') ? 'text-emerald-600' : 'text-amber-500'}`}>
                          {comp.unit === '€' ? `€${comp.value}` : comp.unit === '% minimo garantito' ? `${comp.value}%` : `${comp.value}%`}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm">{comp.description}</p>
                      {comp.name.includes('Protezione') && (
                        <p className="mt-2 text-xs text-emerald-600 font-medium">Minimo garantito all'utente</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Commission Distribution */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Distribuzione della Commissione</CardTitle>
                <p className="text-slate-500">{businessPlan.commission_model.commission_distribution.description}</p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  {businessPlan.commission_model.commission_distribution.recipients.map((recipient, i) => (
                    <div key={i} className={`p-6 rounded-xl ${
                      i === 0 ? 'bg-slate-900 text-white' :
                      i === 1 ? 'bg-amber-500 text-white' :
                      'bg-emerald-500 text-white'
                    }`}>
                      <div className="text-5xl font-bold mb-2">{recipient.percentage}%</div>
                      <h4 className="font-semibold text-lg mb-2">{recipient.name}</h4>
                      <p className="text-sm opacity-90 mb-3">{recipient.role}</p>
                      <p className="text-sm opacity-75">{recipient.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Examples */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Esempi Pratici di Calcolo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {businessPlan.commission_model.examples.map((example, i) => (
                    <div key={i} className={`p-6 rounded-xl border-2 ${
                      example.calculation.is_reduced ? 'border-emerald-300 bg-emerald-50' : 
                      example.calculation.cashback_netto <= 0 ? 'border-red-200 bg-red-50' : 
                      'border-slate-200 bg-white'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-lg">{example.title}</h4>
                          <p className="text-slate-600">{example.scenario}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {example.highlight && (
                            <Badge className="bg-emerald-500 text-white">
                              <Shield className="w-3 h-3 mr-1" />
                              {example.highlight}
                            </Badge>
                          )}
                          <Badge className={example.calculation.is_reduced ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                            {example.input.descrizione}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-4 gap-4 mb-4">
                        <div className="p-4 bg-slate-100 rounded-lg text-center">
                          <p className="text-xs text-slate-500 uppercase">Speso</p>
                          <p className="text-2xl font-bold">€{example.input.importo_speso}</p>
                        </div>
                        <div className="p-4 bg-amber-100 rounded-lg text-center">
                          <p className="text-xs text-amber-600 uppercase">Cashback</p>
                          <p className="text-2xl font-bold">{example.input.sconto_percentuale}%</p>
                        </div>
                        <div className="p-4 bg-slate-100 rounded-lg text-center">
                          <p className="text-xs text-slate-500 uppercase">Cashback Lordo</p>
                          <p className="text-2xl font-bold">€{example.calculation.cashback_lordo}</p>
                        </div>
                        <div className="p-4 bg-emerald-100 rounded-lg text-center">
                          <p className="text-xs text-emerald-600 uppercase">Cashback Netto</p>
                          <p className="text-2xl font-bold text-emerald-700">€{example.calculation.cashback_netto.toFixed(2)}</p>
                        </div>
                      </div>

                      {/* Show reduced rate info if applicable */}
                      {example.calculation.is_reduced && (
                        <div className="p-4 bg-emerald-100 rounded-lg border border-emerald-300 mb-4">
                          <p className="text-sm font-medium text-emerald-800 mb-1">
                            <Shield className="w-4 h-4 inline mr-1" />
                            Tariffa Ridotta Applicata
                          </p>
                          <p className="text-xs text-emerald-700">
                            Commissione standard: €{example.calculation.commissione_standard?.toFixed(2)} → 
                            Commissione applicata: <strong>€{example.calculation.commissione_applicata?.toFixed(2)}</strong>
                          </p>
                          <p className="text-xs text-emerald-700 mt-1">
                            L'utente riceve il {example.calculation.user_percentage}% del cashback (minimo garantito 60%)
                          </p>
                        </div>
                      )}

                      {!example.calculation.is_reduced && (
                        <div className="p-4 bg-slate-50 rounded-lg mb-4">
                          <p className="text-sm font-medium mb-2">Calcolo commissione (tariffa standard):</p>
                          <p className="text-slate-600 text-sm">
                            €{example.calculation.commissione_fissa} (fisso) + €{example.calculation.commissione_percentuale?.toFixed(2)} (10% di €{example.calculation.cashback_lordo}) = 
                            <strong className="text-slate-900"> €{example.calculation.commissione_totale.toFixed(2)}</strong>
                          </p>
                          {example.calculation.user_percentage && (
                            <p className="text-xs text-slate-500 mt-1">
                              L'utente riceve il {example.calculation.user_percentage}% del cashback
                            </p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="p-3 bg-slate-900 text-white rounded-lg text-center">
                          <p className="text-xs opacity-75">Piattaforma (70%)</p>
                          <p className="font-bold">€{example.distribution.piattaforma_aiac.toFixed(2)}</p>
                        </div>
                        <div className="p-3 bg-amber-500 text-white rounded-lg text-center">
                          <p className="text-xs opacity-75">Commerciale (20%)</p>
                          <p className="font-bold">€{example.distribution.commerciale.toFixed(2)}</p>
                        </div>
                        <div className="p-3 bg-emerald-500 text-white rounded-lg text-center">
                          <p className="text-xs opacity-75">Admin Condo (10%)</p>
                          <p className="font-bold">€{example.distribution.admin_condominio.toFixed(2)}</p>
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-slate-600 italic">{example.summary}</p>
                      {example.note && (
                        <p className="mt-2 text-sm text-amber-700 font-medium">{example.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Business Rationale */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Perché Questo Modello?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {businessPlan.commission_model.business_rationale.why_this_model.map((item, i) => (
                    <div key={i} className={`p-4 border rounded-lg ${
                      item.reason === 'Protezione Utente' || item.reason === 'Incentivo Micro-Transazioni' 
                        ? 'border-emerald-300 bg-emerald-50' 
                        : 'border-slate-200'
                    }`}>
                      <h4 className={`font-semibold mb-2 ${
                        item.reason === 'Protezione Utente' || item.reason === 'Incentivo Micro-Transazioni' 
                          ? 'text-emerald-600' 
                          : 'text-amber-600'
                      }`}>{item.reason}</h4>
                      <p className="text-slate-600 text-sm">{item.explanation}</p>
                    </div>
                  ))}
                </div>

                {/* Small Transaction Benefits */}
                {businessPlan.commission_model.business_rationale.small_transaction_benefit && (
                  <div className="p-6 bg-emerald-100 rounded-xl mb-6 border-2 border-emerald-300">
                    <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      {businessPlan.commission_model.business_rationale.small_transaction_benefit.title}
                    </h4>
                    <ul className="space-y-2 mb-4">
                      {businessPlan.commission_model.business_rationale.small_transaction_benefit.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-emerald-700 text-sm">
                          <CheckCircle2 className="w-4 h-4 shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm text-emerald-800 italic">
                      <strong>Trade-off:</strong> {businessPlan.commission_model.business_rationale.small_transaction_benefit.tradeoff}
                    </p>
                  </div>
                )}
                
                <h4 className="font-semibold mb-4">Confronto con Competitor</h4>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="font-medium">vs Groupon:</span>
                    <span className="text-slate-600 ml-2">{businessPlan.commission_model.business_rationale.comparison.vs_groupon}</span>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="font-medium">vs Satispay:</span>
                    <span className="text-slate-600 ml-2">{businessPlan.commission_model.business_rationale.comparison.vs_satispay}</span>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <span className="font-medium">vs Carte Fedeltà:</span>
                    <span className="text-slate-600 ml-2">{businessPlan.commission_model.business_rationale.comparison.vs_carte_fedelta}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Financial Projections Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Proiezioni Finanziarie per Commissioni</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase">Transazione Media</p>
                    <p className="text-2xl font-bold">€{businessPlan.commission_model.financial_projections.avg_transaction}</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-xs text-slate-500 uppercase">Cashback Medio</p>
                    <p className="text-2xl font-bold">{businessPlan.commission_model.financial_projections.avg_discount}%</p>
                  </div>
                  <div className="p-4 bg-amber-100 rounded-lg text-center">
                    <p className="text-xs text-amber-600 uppercase">Commissione Media/Trans.</p>
                    <p className="text-2xl font-bold">€{businessPlan.commission_model.financial_projections.avg_commission_per_transaction}</p>
                  </div>
                  <div className="p-4 bg-emerald-100 rounded-lg text-center">
                    <p className="text-xs text-emerald-600 uppercase">Cashback Netto Utente</p>
                    <p className="text-2xl font-bold">€{businessPlan.commission_model.financial_projections.avg_cashback_net_to_user}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div className="p-4 bg-blue-100 rounded-lg text-center">
                    <p className="text-xs text-blue-600 uppercase">Trans./Utente/Mese</p>
                    <p className="text-2xl font-bold">{businessPlan.commission_model.financial_projections.transactions_per_user_month}</p>
                  </div>
                  <div className="p-4 bg-purple-100 rounded-lg text-center">
                    <p className="text-xs text-purple-600 uppercase">Commissione/Utente/Mese</p>
                    <p className="text-2xl font-bold">€{businessPlan.commission_model.financial_projections.monthly_commission_per_active_user}</p>
                  </div>
                  <div className="p-4 bg-slate-900 text-white rounded-lg text-center">
                    <p className="text-xs opacity-75 uppercase">Commissione/Utente/Anno</p>
                    <p className="text-2xl font-bold">€{businessPlan.commission_model.financial_projections.yearly_commission_per_active_user}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Market Analysis */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <PieChartIcon className="w-8 h-8 text-amber-500" />
            Analisi di Mercato
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500 uppercase tracking-wider">TAM</p>
                <p className="text-4xl font-bold text-slate-900 my-2">{formatNumber(businessPlan.market_analysis.tam.value)}</p>
                <p className="text-sm text-slate-600">{businessPlan.market_analysis.tam.description}</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500 uppercase tracking-wider">SAM</p>
                <p className="text-4xl font-bold text-blue-600 my-2">{formatNumber(businessPlan.market_analysis.sam.value)}</p>
                <p className="text-sm text-slate-600">{businessPlan.market_analysis.sam.description}</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-500 uppercase tracking-wider">SOM</p>
                <p className="text-4xl font-bold text-emerald-600 my-2">{formatNumber(businessPlan.market_analysis.som.value)}</p>
                <p className="text-sm text-slate-600">{businessPlan.market_analysis.som.description}</p>
              </CardContent>
            </Card>
          </div>

          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Categorie Aziende Partner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3">
                  {Object.entries(businessPlan.company_categories).map(([name, data], index) => (
                    <div key={name} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{name}</p>
                        <p className="text-xs text-slate-500">{data.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">€{data.avg_ticket} medio</p>
                        <p className="text-xs text-slate-500">{data.avg_discount}% cashback</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* 5-Year Projections */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-amber-500" />
            Proiezioni Quinquennali
          </h2>

          {/* Growth Chart */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Crescita Utenti e Aziende</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={yearlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                    <Area yAxisId="left" type="monotone" dataKey="utenti" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Utenti" />
                    <Area yAxisId="right" type="monotone" dataKey="aziende" stackId="2" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name="Aziende" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Revenue & Profitability */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Revenue e Margine</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `€${(v/1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#10B981" name="Revenue" />
                    <Bar dataKey="costi" fill="#EF4444" name="Costi" />
                    <Bar dataKey="margine" fill="#3B82F6" name="Margine" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Penetration */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Penetrazione di Mercato (%)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={penetrationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis domain={[0, 60]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="condomini" stroke="#0F172A" strokeWidth={2} name="Condomini attivi" />
                    <Line type="monotone" dataKey="utenti" stroke="#F59E0B" strokeWidth={2} name="Utenti in condomini attivi" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Yearly Details Table */}
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio Annuale</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold">Metrica</th>
                      {businessPlan.yearly_projections.map(y => (
                        <th key={y.year} className="text-right py-3 px-4 font-semibold">Anno {y.year}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Condomini Attivi</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4">{formatNumber(y.metrics.active_condos)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td className="py-3 px-4 font-medium">Utenti Attivi</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4 font-semibold">{formatNumber(y.metrics.active_users)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Aziende Partner</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4">{formatNumber(y.metrics.total_companies)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Transazioni/Anno</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4">{formatNumber(y.metrics.yearly_transactions)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100 bg-emerald-50">
                      <td className="py-3 px-4 font-medium text-emerald-700">Revenue Totale</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4 font-semibold text-emerald-700">{formatCurrency(y.revenue.total_revenue)}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium text-red-600">Costi Totali</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4 text-red-600">{formatCurrency(y.costs.total_costs)}</td>
                      ))}
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="py-3 px-4 font-bold text-blue-700">Margine Lordo</td>
                      {businessPlan.yearly_projections.map(y => (
                        <td key={y.year} className="text-right py-3 px-4 font-bold text-blue-700">
                          {formatCurrency(y.profitability.gross_margin)}
                          <span className="text-xs ml-1">({y.profitability.margin_percentage}%)</span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* KPIs */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-amber-500" />
            KPI Target
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(businessPlan.kpis).map(([key, kpi]) => (
              <Card key={key}>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">{key.replace(/_/g, ' ')}</p>
                  <div className="flex items-end gap-4 mb-2">
                    <div>
                      <p className="text-xs text-slate-400">Anno 1</p>
                      <p className="text-xl font-bold text-slate-900">
                        {key.includes('cost') || key.includes('value') ? `€${kpi.year_1}` : `${kpi.year_1}%`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Anno 5</p>
                      <p className="text-xl font-bold text-emerald-600">
                        {key.includes('cost') || key.includes('value') ? `€${kpi.year_5}` : `${kpi.year_5}%`}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{kpi.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Milestones */}
        <section className="mb-12 print:mb-8 print:break-after-page">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-amber-500" />
            Roadmap & Milestones
          </h2>
          
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                {businessPlan.milestones.map((m, i) => (
                  <div key={i} className="flex gap-4 mb-6 last:mb-0">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </div>
                      {i < businessPlan.milestones.length - 1 && (
                        <div className="w-0.5 h-full bg-amber-200 my-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <Badge className="bg-slate-100 text-slate-700 mb-2">{m.quarter}</Badge>
                      <p className="font-medium text-slate-900">{m.milestone}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Risks */}
        <section className="mb-12 print:mb-8">
          <h2 className="text-3xl font-bold text-slate-900 font-serif mb-6 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            Rischi e Mitigazioni
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {businessPlan.risks.map((r, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">{r.risk}</p>
                      <p className="text-sm text-slate-600">
                        <span className="font-medium text-emerald-600">Mitigazione:</span> {r.mitigation}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-200 pt-8 mt-12 print:mt-8">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div>
              <p className="font-semibold text-slate-700">AIAC-Deals</p>
              <p>Associazione Italiana Amministratori di Condominio</p>
            </div>
            <div className="text-right">
              <p>Documento Confidenziale</p>
              <p>© 2024 Tutti i diritti riservati</p>
            </div>
          </div>
        </footer>
      </main>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:break-after-page {
            break-after: page;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BusinessPlanPage;
